import { useEffect, useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";
import type { DefaultValues } from "react-hook-form";
import { Button, Form } from "../ui";
import { useTranslation } from "react-i18next";
import { generateFields } from "./fields";
import { BulkEditableTable } from "./so-bulk-form";
import {
  getRerenderFieldNames,
  zodSchemaFromStructure,
} from "~/utils/form-util";
import type { ZodTypeAny } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import type { BulkFormData, FormStructure, SoFormProps } from "~/types/ui";
import { SteppedForm } from "./so-stepped-form";

export function formFromStructure<T>(
  structure: FormStructure<T>,
): DefaultValues<Record<keyof T, any>> {
  const form = {} as Record<keyof T, any>;
  (Object.keys(structure) as (keyof T | "virtualFields")[]).forEach((k) => {
    if (k === "virtualFields") return;
    const def = structure[k];
    if (def) {
      form[k as keyof T] = def.default ?? "";
    }
  });

  return form as DefaultValues<Record<keyof T, any>>;
}

export function SoForm<T>(props: SoFormProps<T>) {
  if (props.isStepsForm)
    return (
      <SteppedForm
        values={props.values}
        structure={props.structure}
        onSubmit={(data: T | BulkFormData<T>) => {
          props.onSubmit(data);
        }}
        stepsOptions={props.stepsOptions ?? []}
        isBulkForm={props.isBulkForm}
      />
    );
  return props.isBulkForm ? (
    <BulkEditableTable
      data={props.values as T[]}
      structure={props.structure}
      onSubmit={props.onSubmit}
      disableRowAlteration={props?.disableRowAlteration}
      hideSearchbar={props?.hideSearchbar}
      overrideSaveButtonLable={props?.overrideSaveButtonLable}
    />
  ) : (
    <BaseForm
      onSubmit={(data: T | BulkFormData<T>) => {
        props.onSubmit(data);
      }}
      structure={props.structure}
      onChange={props.onChange}
      overrideSaveButtonLable={props?.overrideSaveButtonLable}
    />
  );
}

function BaseForm<T>({
  structure,
  onSubmit,
  onChange,
  overrideSaveButtonLable,
}: SoFormProps<T>) {
  const { t } = useTranslation();

  const schema: ZodTypeAny = useMemo(
    () => zodSchemaFromStructure(structure, t),
    [structure, t],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: formFromStructure(structure),
  });

  const watchedNamesFromStructure = getRerenderFieldNames(structure);
  const watchedValues = useWatch({
    control: form.control,
    name: watchedNamesFromStructure,
  });

  useEffect(() => {
    const subscription = form.watch((values) => {
      if (onChange) {
        onChange(values as any);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  const fields = useMemo(() => {
    return generateFields(form, structure, t);
  }, [form, structure, t, watchedValues]);

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit(onSubmit as any)();
        }}
        className="space-y-6"
      >
        {fields}

        <Button type="submit">
          {t(overrideSaveButtonLable ?? "common.save")}
        </Button>
      </form>
    </Form>
  );
}
